import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Moon, Sun, Monitor, Download, CloudOff, MessageSquare } from 'lucide-react';
import { CustomSelect } from './controls/CustomSelect';

import './Header.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const Header: React.FC<{ activeTab: 'create' | 'batch', onTabChange: (tab: 'create' | 'batch') => void }> = ({ activeTab, onTabChange }) => {
  const { t, i18n } = useTranslation('common');
  const [theme, setTheme] = useState(localStorage.getItem('MamaQR-theme') ?? 'system');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showInstall, setShowInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handleOnline = () => { setIsOffline(false); };
    const handleOffline = () => { setIsOffline(true); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const dismissed = localStorage.getItem('MamaQR-install-dismissed');
      if (dismissed) {
        const time = parseInt(dismissed, 10);
        if (Date.now() - time < 30 * 24 * 60 * 60 * 1000) return;
      }
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') {
      localStorage.setItem('MamaQR-install-dismissed', Date.now().toString());
    }
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  const handleThemeChange = (value: string) => {
    setTheme(value);
    localStorage.setItem('MamaQR-theme', value);
    
    let activeTheme = value;
    if (value === 'system') {
      activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    document.documentElement.setAttribute('data-theme', activeTheme);
    requestAnimationFrame(() => {
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--color-bg-primary').trim();
      if (bgColor) {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bgColor);
      }
    });
  };

  const handleLanguageChange = (value: string) => {
    void i18n.changeLanguage(value);
  };

  return (
    <header className="header">
      <div className="header-left">
        <div className="flex-row-center-gap-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 256 256" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round">
            <rect x="32" y="32" width="72" height="72" rx="8" />
            <rect x="152" y="32" width="72" height="72" rx="8" />
            <rect x="32" y="152" width="72" height="72" rx="8" />
            <rect x="56" y="56" width="24" height="24" />
            <rect x="176" y="56" width="24" height="24" />
            <rect x="56" y="176" width="24" height="24" />
            <path d="M152 152h24v24h-24z" />
            <path d="M200 152h24v24h-24z" />
            <path d="M152 200h24v24h-24z" />
            <path d="M200 200h24v24h-24z" />
          </svg>
          <h1 className="logo m-0">
            <span className="sr-only">{t('header.srTitle', 'Free QR Code Generator - ')}</span>
            {t('header.brandName', 'MamaQR')}
          </h1>
        </div>
      </div>

      <div className="header-nav">
        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`} onClick={() => { onTabChange('create'); }}>
            {t('header.create', 'Create')}
          </button>
          <button className={`tab-btn ${activeTab === 'batch' ? 'active' : ''}`} onClick={() => { onTabChange('batch'); }}>
            {t('header.batch', 'Batch')}
          </button>
        </div>
      </div>


      <div className="header-right">
        {isOffline && (
          <div className="offline-indicator">
            <CloudOff size={14} />
            <span>{t('header.offline', 'Offline')}</span>
          </div>
        )}
        
        {showInstall && (
          <button className="install-btn" onClick={() => { void handleInstall(); }}>
            <Download size={16} />
            <span className="install-label">{t('header.install', 'Install')}</span>
          </button>
        )}

        <a 
          href="https://github.com/mama-tools/mama-tools-support/issues/new/choose" 
          target="_blank" 
          rel="noopener noreferrer"
          className="header-icon-btn"
          aria-label={t('header.feedback', 'Feedback & Support')}
          title={t('header.feedback', 'Feedback & Support')}
        >
          <MessageSquare size={16} />
        </a>

        <CustomSelect
          value={i18n.language.split('-')[0]}
          onChange={handleLanguageChange}
          icon={<Globe size={16} />}
          ariaLabel={t('header.language', 'Language')}
          options={[
            { value: 'en', label: 'English' },
            { value: 'ar', label: 'العربية' },
          ]}
        />

        <CustomSelect
          value={theme}
          onChange={handleThemeChange}
          icon={theme === 'system' ? <Monitor size={16} /> : theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
          ariaLabel={t('header.theme', 'Theme')}
          options={[
            { value: 'system', label: t('theme.system', 'System') },
            { value: 'light', label: t('theme.light', 'Light') },
            { value: 'dark', label: t('theme.dark', 'Dark') },
          ]}
        />
      </div>
    </header>
  );
};
